import { Plus } from 'lucide-react'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import type { RelationRef } from '../timeline/types'
import { AgreementCard } from './AgreementCard'
import type { Agreement, RelationAgreements } from './types'

/**
 * The tab "Afspraken" (design §4.4/§4.5): Lopend, Gepauzeerd and a collapsed Beëindigd; on an
 * organisation page also "Met contactpersonen" (their personal agreements, read only, with a link).
 * On a contact page the agreements it is contact person of ("via YES!Delft") are interactive.
 */
export function AgreementsTab({
  relation,
  name,
  data,
  failed,
  onNew,
  onEdit,
  onContact,
  onChanged,
}: {
  relation: RelationRef
  name: string
  data: RelationAgreements | null
  failed: boolean
  onNew: () => void
  onEdit: (agreement: Agreement) => void
  onContact: (agreement: Agreement) => void
  onChanged: () => void
}) {

  const organisationPage = relation.kind === 'organisation'
  // On a contact page the via agreements belong to the contact's organisations: interactive, own and via together.
  const mine = data ? (organisationPage ? data.own : [...data.own, ...data.via]) : []
  const viaIds = new Set(data?.via.map((agreement) => agreement.id))
  const running = mine.filter((agreement) => agreement.status === 'actief')
  const paused = mine.filter((agreement) => agreement.status === 'gepauzeerd')
  const ended = mine.filter((agreement) => agreement.status === 'beeindigd')

  function cards(list: Agreement[], readOnly = false) {
    return list.map((agreement) => (
      <div key={agreement.id} className="flex flex-col gap-1">
        <AgreementCard
          agreement={agreement}
          readOnly={readOnly}
          showParty={viaIds.has(agreement.id)}
          onEdit={onEdit}
          onContact={onContact}
          onChanged={onChanged}
        />
        {readOnly && agreement.person ? (
          <Link
            to={`/relaties/contacten/${agreement.person.id}?tab=afspraken`}
            className="self-start text-sm text-primary hover:underline max-md:min-h-11"
          >
            Naar contact →
          </Link>
        ) : null}
      </div>
    ))
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-base font-semibold">Afspraken</h2>
        <Button type="button" size="sm" variant="outline" onClick={onNew}>
          <Plus />
          Afspraak
        </Button>
      </div>
      {failed ? <p className="text-sm text-destructive">Afspraken laden mislukt.</p> : null}
      {data === null && !failed ? <p className="text-sm text-muted-foreground">Laden…</p> : null}
      {data && mine.length === 0 && (!organisationPage || data.via.length === 0) ? (
        <p className="text-sm text-muted-foreground">Nog geen afspraken. Leg vast wat VONK met {name} afspreekt.</p>
      ) : null}
      {running.length > 0 ? (
        <section aria-label="Lopend" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Lopend</h3>
          {cards(running)}
        </section>
      ) : null}
      {paused.length > 0 ? (
        <section aria-label="Gepauzeerd" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Gepauzeerd</h3>
          {cards(paused)}
        </section>
      ) : null}
      {organisationPage && data && data.via.length > 0 ? (
        <section aria-label="Met contactpersonen" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Met contactpersonen</h3>
          {cards(data.via, true)}
        </section>
      ) : null}
      {ended.length > 0 ? (
        <details className="flex flex-col gap-2">
          <summary className="cursor-pointer text-sm font-semibold max-md:min-h-11">Beëindigd ({ended.length})</summary>
          <div className="mt-2 flex flex-col gap-2">{cards(ended)}</div>
        </details>
      ) : null}
    </div>
  )
}
